/**
 * Search Queries Management Page
 * Allows users to create and manage search queries for job scraping
 * Uses the universal Table component from Storybook
 */

import { useState, useMemo, useCallback } from 'react'

import AddJobDrawer from './AddJobDrawer'
import FilterBar from './FilterBar'
import { AddQueryForm } from './SearchQueries/AddQueryForm'
import { useSearchQueriesColumns } from './SearchQueries/columns'
import { useRealtimeSubscriptionsEffect } from './SearchQueries/effects'
import { FailedJobsModal } from './SearchQueries/FailedJobsModal'
import { handleViewFailedJobs, handleDeleteQuery, handleAddQuery, handleSort, handleRowClick } from './SearchQueries/handlers'
import { sortQueries } from './SearchQueries/sorting'
import { SearchQuery, FailedJobAttempt, SearchPlatform } from './SearchQueries/types'
import { Table } from './Table'


function SearchQueries() {
  const [queries, setQueries] = useState<SearchQuery[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [_realtimeStatus, setRealtimeStatus] = useState<'connecting' | 'connected' | 'disconnected'>('connecting')
  const [failedJobsModal, setFailedJobsModal] = useState<{ queryId: string; queryName: string } | null>(null)
  const [failedJobs, setFailedJobs] = useState<FailedJobAttempt[]>([])
  const [loadingFailedJobs, setLoadingFailedJobs] = useState(false)
  const [errorTypeFilter, setErrorTypeFilter] = useState<string | null>(null)
  const [errorCategoryFilter, setErrorCategoryFilter] = useState<string | null>(null)
  const [sortColumn, setSortColumn] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [formData, setFormData] = useState<{ name: string; search_url: string; platform: SearchPlatform }>({
    name: '',
    search_url: '',
    platform: 'linkedin'
  })

  // Wrapper functions for handlers
  const handleViewFailedJobsWrapper = useCallback(async (queryId: string, queryName: string) => {
    await handleViewFailedJobs(
      queryId,
      queryName,
      setFailedJobsModal,
      setLoadingFailedJobs,
      setFailedJobs
    )
  }, [])

  const handleDeleteQueryWrapper = useCallback(async (id: string) => {
    await handleDeleteQuery(id, queries, setError, setQueries)
  }, [queries])

  // Define table columns with custom rendering
  const columns = useSearchQueriesColumns(handleViewFailedJobsWrapper, handleDeleteQueryWrapper)

  // Setup realtime subscriptions
  useRealtimeSubscriptionsEffect(setLoading, setError, setQueries, setRealtimeStatus)



  // Filter queries based on search
  const filteredQueries = useMemo(() => 
    queries.filter(query =>
      query.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      query.search_url.toLowerCase().includes(searchQuery.toLowerCase())
    ),
    [queries, searchQuery]
  )

  // Wrapper functions for handlers
  const handleSortWrapper = (column: string) => {
    handleSort(column, sortColumn, sortDirection, setSortColumn, setSortDirection)
  }

  const handleAddQueryWrapper = async (e: React.FormEvent) => {
    await handleAddQuery(e, formData, setError, setQueries, setFormData, setIsDrawerOpen)
  }

  const sortedFilteredQueries = useMemo(() => {
    return sortQueries(filteredQueries, sortColumn, sortDirection)
  }, [filteredQueries, sortColumn, sortDirection])

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="p-6 pb-0 flex-shrink-0">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-h1 font-semibold text-text-primary">Search Queries</h1>
        </div>
        <FilterBar 
          searchQuery={searchQuery} 
          onSearchChange={setSearchQuery}
          onAddJob={() => setIsDrawerOpen(true)}
          onBulkDelete={() => {}}
          selectedCount={0}
          searchPlaceholder="Search queries by name or URL..."
          addButtonLabel="Add Search Query"
        />
      </div>
      
      <div className="flex-1 overflow-auto px-6 pb-6">
        {error && (
          <div className="mb-4 p-4 bg-danger-subtle border border-danger/30 rounded-lg text-danger">
            {error}
          </div>
        )}

        <div style={{ height: '100%', minHeight: '500px' }}>
          <Table
            columns={columns}
            data={sortedFilteredQueries}
            loading={loading}
            error={error}
            onRowClick={handleRowClick}
            sortable
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSortWrapper}
            showColumnManagement={true}
            emptyMessage={searchQuery ? 'No search queries found' : 'No search queries yet'}
            renderEmptyState={() => (
              <div className="text-center py-12">
                <p className="text-text-muted mb-4">
                  {searchQuery ? 'No search queries found' : 'No search queries yet'}
                </p>
                <button
                  onClick={() => setIsDrawerOpen(true)}
                  className="px-4 py-2 bg-brand-primary text-text-inverse rounded-lg hover:bg-brand-primary-hover transition-colors min-h-11"
                >
                  Add your first query
                </button>
              </div>
            )}
          />
        </div>
      </div>

      <AddJobDrawer
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false)
          setFormData({ name: '', search_url: '', platform: 'linkedin' })
        }}
        onJobAdded={() => {}}
        customContent={
          <AddQueryForm
            formData={formData}
            setFormData={setFormData}
            handleAddQuery={handleAddQueryWrapper}
            setIsDrawerOpen={setIsDrawerOpen}
          />
        }
      />

      <FailedJobsModal
        failedJobsModal={failedJobsModal}
        failedJobs={failedJobs}
        loadingFailedJobs={loadingFailedJobs}
        errorTypeFilter={errorTypeFilter}
        errorCategoryFilter={errorCategoryFilter}
        setFailedJobsModal={setFailedJobsModal}
        setFailedJobs={setFailedJobs}
        setErrorTypeFilter={setErrorTypeFilter}
        setErrorCategoryFilter={setErrorCategoryFilter}
      />
    </div>
  )
}

export default SearchQueries
